import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { HardDrive } from "lucide-react";
import type { DeviceKeep } from "@/hooks/useDeviceKeep";

export function formatSavedAt(iso: string | null, locale?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try { return d.toLocaleString(locale || undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return d.toISOString(); }
}

/**
 * The opt-in control for keeping a tool's entries on this device. Off by default. Reusable: pass what the tool
 * keeps and what it does not. The wording states the privacy promise in both states.
 */
export function DeviceKeepPanel({ keep, kept, notKept, locale }: { keep: DeviceKeep; /** e.g. "your entries, filters and settings" */ kept: string; /** e.g. "Receipt files are not kept." */ notKept?: string; locale?: string }) {
  const id = useId();
  const [confirming, setConfirming] = useState(false);
  const when = formatSavedAt(keep.savedAt, locale);
  return (
    <section className="rounded-lg border p-3 text-sm space-y-2" aria-label="Keep entries on this device" data-testid="keep-panel">
      <label className="flex items-start gap-2 cursor-pointer" htmlFor={id}>
        <input id={id} type="checkbox" className="mt-1 h-4 w-4" checked={keep.on} onChange={(e) => keep.setOn(e.target.checked)} data-testid="keep-toggle" />
        <span><strong>Keep my entries in this browser</strong>
          <span className="block text-xs text-muted-foreground">
            {keep.on
              ? `On. ${kept[0].toUpperCase()}${kept.slice(1)} are saved in this browser on this device and are still here next time you visit. ${notKept ?? ""} Nothing is sent to our servers.`
              : `Off (the default). Everything is forgotten when you close this tab and nothing is saved on your device. Turn this on to keep ${kept} in this browser between visits. They stay on this device and are never sent to our servers.${notKept ? ` ${notKept}` : ""}`}
          </span>
        </span>
      </label>
      {keep.on && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="keep-status">
          <HardDrive className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{when ? `Saved on this device, last saved ${when}.` : "Saved on this device as soon as you add an entry."}</span>
        </div>
      )}
      {keep.problem && <p className="text-xs text-red-600" role="alert" data-testid="keep-problem">{keep.problem}</p>}
      {keep.on && (confirming ? (
        <div className="flex flex-wrap items-center gap-2 text-xs" role="alertdialog" aria-label="Delete everything saved here?">
          <span>Delete the copy saved on this device and stop keeping entries? What is on screen now stays until you close the tab.</span>
          <Button size="sm" variant="destructive" onClick={() => { keep.forget(); setConfirming(false); }} data-testid="keep-forget-yes">Delete it</Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setConfirming(true)} data-testid="keep-forget">Delete everything saved here</Button>
      ))}
    </section>
  );
}
